import React, { useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';

const debugKeys = [
  // Tipo di competizione
  'competitionType',
  // Configurazione mista
  'mixed_config',
  // Configurazione individuale
  'individual_numPools',
  'individual_schema',
  'individual_manual_pools',
  'individual_sbarramento',
  'individual_stoccateGirone',
  'individual_stoccateElimDiretta',
  // Dati condivisi
  'teams',
  'matches',
  'bracket',
  // Ultimo match selezionato
  'lastMatchId',
  'stoccatePerAssalto'
];

function getLocalStorageDump() {
  const dump = {};
  debugKeys.forEach(key => {
    try {
      dump[key] = localStorage.getItem(key);
    } catch (e) {
      dump[key] = `Errore: ${e.message}`;
    }
  });
  return dump;
}

const DebugDataFlow = () => {
  const [data, setData] = useState(getLocalStorageDump());
  const [expandedView, setExpandedView] = useState({});

  const handleRefresh = () => {
    setData(getLocalStorageDump());
  };

  const handleClearKey = (key) => {
    localStorage.removeItem(key);
    setData(getLocalStorageDump());
  };

  const toggleExpanded = (key) => {
    setExpandedView(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const parseJson = (jsonString) => {
    try {
      return JSON.parse(jsonString || '{}');
    } catch (e) {
      return { error: e.message };
    }
  };

  // Ottieni il tipo di competizione corrente (rimossa variabile inutilizzata)

  // Informazioni riassuntive
  const getCompetitionSummary = () => {
    try {
      // Tipo di competizione
      const type = localStorage.getItem('competitionType') || 'individual';
      let summary = `Tipo: ${type}\n`;

      // Squadre
      const teams = parseJson(localStorage.getItem('teams'));
      summary += `Squadre: ${Array.isArray(teams) ? teams.length : 0}\n`;

      // Match
      const matches = parseJson(localStorage.getItem('matches'));
      summary += `Match: ${Array.isArray(matches) ? matches.length : 0}\n`;

      // Dettagli specifici per tipo
      if (type === 'individual') {
        const numPools = localStorage.getItem('individual_numPools') || '1';
        const stoccateGirone = localStorage.getItem('individual_stoccateGirone') || '5';
        summary += `Gironi: ${numPools}, Stoccate: ${stoccateGirone}`;
      } else if (type === 'mixed') {
        const config = parseJson(localStorage.getItem('mixed_config'));
        const categories = config.categories || [];
        summary += `Categorie: ${categories.join(', ')}\nStoccate: ${config.stoccate || 5}`;
      }

      return summary;
    } catch (e) {
      return `Errore nel riepilogo: ${e.message}`;
    }
  };

  // Verifica della struttura dei match
  const getMatchesAnalysis = () => {
    try {
      const matches = parseJson(localStorage.getItem('matches'));
      if (!Array.isArray(matches) || matches.length === 0) return 'Nessun match';

      let analysis = `Trovati ${matches.length} match:\n`;

      // Analizza il primo match
      const firstMatch = matches[0];
      analysis += `\nPrimo match:\n`;
      analysis += `- Squadra A: ${firstMatch.squadraA || firstMatch.team1?.name || 'N/A'}\n`;
      analysis += `- Squadra B: ${firstMatch.squadraB || firstMatch.team2?.name || 'N/A'}\n`;
      analysis += `- Categoria: ${firstMatch.categoria || 'N/A'}\n`;
      analysis += `- Stoccate: ${firstMatch.stoccate || 'N/A'}\n`;
      analysis += `- Stato: ${firstMatch.stato || 'N/A'}\n`;
      analysis += `- Assalti: ${Array.isArray(firstMatch.assalti) ? firstMatch.assalti.length : 'Nessuno'}\n`;

      // Se ci sono categorie separate da virgola, è un caso speciale
      if (firstMatch.categoria && firstMatch.categoria.includes(',')) {
        analysis += `\nRilevato match misto con categorie unite!`;
      }

      return analysis;
    } catch (e) {
      return `Errore nell'analisi match: ${e.message}`;
    }
  };

  return (
    <ScrollView style={styles.container}>
      <Text style={styles.title}>Debug Flusso Dati (LocalStorage)</Text>

      <View style={styles.summaryCard}>
        <Text style={styles.summaryTitle}>Riepilogo Competizione</Text>
        <Text style={styles.summaryText}>{getCompetitionSummary()}</Text>
      </View>

      <View style={styles.summaryCard}>
        <Text style={styles.summaryTitle}>Analisi Match</Text>
        <Text style={styles.summaryText}>{getMatchesAnalysis()}</Text>
      </View>

      <TouchableOpacity style={styles.refreshBtn} onPress={handleRefresh}>
        <Text style={styles.refreshBtnText}>Aggiorna Dati</Text>
      </TouchableOpacity>

      <Text style={styles.sectionTitle}>Dati principali</Text>
      {debugKeys.filter(key => ['competitionType', 'teams', 'matches', 'mixed_config'].includes(key)).map(key => (
        <View key={key} style={[styles.itemRow, { borderLeftWidth: 4, borderLeftColor: '#1976d2' }]}>
          <View style={styles.headerRow}>
            <Text style={styles.keyText}>{key}</Text>
            <TouchableOpacity style={styles.expandBtn} onPress={() => toggleExpanded(key)}>
              <Text style={styles.expandBtnText}>{expandedView[key] ? 'Comprimi' : 'Espandi'}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.clearBtn} onPress={() => handleClearKey(key)}>
              <Text style={styles.clearBtnText}>Svuota</Text>
            </TouchableOpacity>
          </View>
          <ScrollView style={[styles.valueBox, expandedView[key] && styles.expandedValueBox]}>
            <Text selectable style={styles.valueText}>
              {expandedView[key] && (key.includes('teams') || key.includes('matches') || key.includes('config')) ?
                JSON.stringify(parseJson(data[key]), null, 2) :
                data[key]}
            </Text>
          </ScrollView>
        </View>
      ))}

      <Text style={styles.sectionTitle}>Altri dati</Text>
      {debugKeys.filter(key => !['competitionType', 'teams', 'matches', 'mixed_config'].includes(key)).map(key => (
        <View key={key} style={styles.itemRow}>
          <View style={styles.headerRow}>
            <Text style={styles.keyText}>{key}</Text>
            <TouchableOpacity style={styles.expandBtn} onPress={() => toggleExpanded(key)}>
              <Text style={styles.expandBtnText}>{expandedView[key] ? 'Comprimi' : 'Espandi'}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.clearBtn} onPress={() => handleClearKey(key)}>
              <Text style={styles.clearBtnText}>Svuota</Text>
            </TouchableOpacity>
          </View>
          <ScrollView style={[styles.valueBox, expandedView[key] && styles.expandedValueBox]}>
            <Text selectable style={styles.valueText}>
              {expandedView[key] && (key.includes('pool') || key.includes('bracket')) ?
                JSON.stringify(parseJson(data[key]), null, 2) :
                data[key]}
            </Text>
          </ScrollView>
        </View>
      ))}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: '#f8f8ff',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1976d2',
    marginBottom: 16,
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0d47a1',
    marginTop: 16,
    marginBottom: 12,
    padding: 8,
    backgroundColor: '#e3f2fd',
    borderRadius: 8,
  },
  summaryCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 16,
    marginBottom: 20,
    elevation: 4,
    shadowColor: '#0a2351',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    borderLeftWidth: 5,
    borderLeftColor: '#2196f3'
  },
  summaryTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1976d2',
    marginBottom: 10,
  },
  summaryText: {
    color: '#37474f',
    fontFamily: 'monospace',
    fontSize: 14,
    lineHeight: 20,
  },
  refreshBtn: {
    backgroundColor: '#1976d2',
    borderRadius: 8,
    padding: 12,
    marginVertical: 16,
    alignSelf: 'center',
    minWidth: 120,
  },
  refreshBtnText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 16,
    textAlign: 'center',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  itemRow: {
    marginBottom: 18,
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 12,
    elevation: 2,
    shadowColor: '#1976d2',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
  },
  keyText: {
    fontWeight: 'bold',
    color: '#1976d2',
    marginRight: 'auto',
    fontSize: 15,
  },
  clearBtn: {
    backgroundColor: '#d32f2f',
    borderRadius: 6,
    padding: 6,
    marginLeft: 8,
  },
  clearBtnText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 13,
  },
  expandBtn: {
    backgroundColor: '#388e3c',
    borderRadius: 6,
    padding: 6,
  },
  expandBtnText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 13,
  },
  valueBox: {
    backgroundColor: '#f4f4fa',
    borderRadius: 6,
    padding: 8,
    minHeight: 38,
    maxHeight: 120,
  },
  expandedValueBox: {
    maxHeight: 400, // molto più alto quando espanso
  },
  valueText: {
    color: '#333',
    fontFamily: 'monospace',
    fontSize: 13,
  },
});

export default DebugDataFlow;
